import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, Check, X, ChevronDown, ChevronRight } from 'lucide-react';

function TaxonomyManager() {
  const [taxonomy, setTaxonomy] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedAreas, setExpandedAreas] = useState({});
  const [newAreaName, setNewAreaName] = useState('');
  const [addingArea, setAddingArea] = useState(false);
  const [conversations, setConversations] = useState({ 'Conversation (Enterprise & SMC)': [], 'Conversation (SMB)': [] });
  const [newConversationInputs, setNewConversationInputs] = useState({});
  const [addingConversationTo, setAddingConversationTo] = useState(null);

  useEffect(() => {
    fetchTaxonomy();
    fetchConversations();
  }, []);

  const fetchTaxonomy = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/taxonomy`);
      if (response.ok) {
        const data = await response.json();
        setTaxonomy(data);
        // Expand all areas by default
        const expanded = {};
        Object.keys(data).forEach(area => expanded[area] = true);
        setExpandedAreas(expanded);
      }
    } catch (error) {
      toast.error('Failed to load taxonomy');
    } finally {
      setLoading(false);
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/taxonomy/conversations`);
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || { 'Conversation (Enterprise & SMC)': [], 'Conversation (SMB)': [] });
      }
    } catch (error) {
      toast.error('Failed to load Conversation taxonomy');
    }
  };

  const handleAddConversation = async (group) => {
    const name = (newConversationInputs[group] || '').trim();
    if (!name) {
      toast.error('Please enter a value name');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/taxonomy/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ group, name })
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations);
        setNewConversationInputs(prev => ({ ...prev, [group]: '' }));
        setAddingConversationTo(null);
        toast.success(`"${name}" added under "${group}"`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add value');
      }
    } catch (error) {
      toast.error('Failed to add value');
    }
  };

  const handleDeleteConversation = async (group, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}" from "${group}"?`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/taxonomy/conversations/${encodeURIComponent(group)}/${encodeURIComponent(name)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations);
        toast.success(`"${name}" deleted from "${group}"`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete value');
      }
    } catch (error) {
      toast.error('Failed to delete value');
    }
  };

  const toggleArea = (area) => {
    setExpandedAreas(prev => ({ ...prev, [area]: !prev[area] }));
  };

  const handleAddArea = async () => {
    if (!newAreaName.trim()) {
      toast.error('Please enter a solution area name');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/taxonomy/solution-area`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: newAreaName.trim() })
      });

      if (response.ok) {
        const data = await response.json();
        setTaxonomy(data.taxonomy);
        setExpandedAreas(prev => ({ ...prev, [newAreaName.trim()]: true }));
        setNewAreaName('');
        setAddingArea(false);
        toast.success(`Solution area "${newAreaName.trim()}" added`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add solution area');
      }
    } catch (error) {
      toast.error('Failed to add solution area');
    }
  };

  const handleDeleteArea = async (areaName) => {
    if (!window.confirm(`Are you sure you want to delete "${areaName}" and all its solution plays?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/taxonomy/solution-area/${encodeURIComponent(areaName)}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTaxonomy(data.taxonomy);
        toast.success(`Solution area "${areaName}" deleted`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete solution area');
      }
    } catch (error) {
      toast.error('Failed to delete solution area');
    }
  };

  const handleDeletePlay = async (areaName, playName) => {
    if (!window.confirm(`Are you sure you want to delete "${playName}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/taxonomy/solution-play?area=${encodeURIComponent(areaName)}&play=${encodeURIComponent(playName)}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTaxonomy(data.taxonomy);
        toast.success(`Solution play "${playName}" deleted`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete solution play');
      }
    } catch (error) {
      toast.error('Failed to delete solution play');
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <p style={{ color: '#6B7280' }}>Loading taxonomy...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937', marginBottom: '4px' }}>
            Solution Taxonomy Manager
          </h2>
          <p style={{ fontSize: '14px', color: '#6B7280' }}>
            Manage Solution Areas for content categorization. Solution Plays (below) are FY26 legacy — kept for existing content only; new Solution Plays can no longer be added.
          </p>
        </div>
        
        {!addingArea ? (
          <button
            onClick={() => setAddingArea(true)}
            data-testid="add-solution-area-btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#1B5E5F',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 16px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            <Plus size={16} />
            Add Solution Area
          </button>
        ) : (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="text"
              value={newAreaName}
              onChange={(e) => setNewAreaName(e.target.value)}
              placeholder="Enter solution area name..."
              autoFocus
              style={{
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #D1D5DB',
                fontSize: '14px',
                width: '250px'
              }}
              onKeyPress={(e) => e.key === 'Enter' && handleAddArea()}
            />
            <button
              onClick={handleAddArea}
              style={{
                backgroundColor: '#10B981',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                padding: '10px',
                cursor: 'pointer'
              }}
            >
              <Check size={16} />
            </button>
            <button
              onClick={() => { setAddingArea(false); setNewAreaName(''); }}
              style={{
                backgroundColor: '#EF4444',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                padding: '10px',
                cursor: 'pointer'
              }}
            >
              <X size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Taxonomy Tree */}
      <div style={{ 
        backgroundColor: '#FFFFFF', 
        borderRadius: '12px', 
        border: '1px solid #E5E7EB',
        overflow: 'hidden'
      }}>
        {Object.keys(taxonomy).length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <p style={{ color: '#6B7280' }}>No solution areas defined. Add one to get started.</p>
          </div>
        ) : (
          Object.entries(taxonomy).map(([areaName, plays], index) => (
            <div 
              key={areaName}
              style={{ 
                borderBottom: index < Object.keys(taxonomy).length - 1 ? '1px solid #E5E7EB' : 'none'
              }}
            >
              {/* Solution Area Header */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px 20px',
                  backgroundColor: expandedAreas[areaName] ? '#F9FAFB' : '#FFFFFF',
                  cursor: 'pointer'
                }}
                onClick={() => toggleArea(areaName)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {expandedAreas[areaName] ? (
                    <ChevronDown size={18} color="#6B7280" />
                  ) : (
                    <ChevronRight size={18} color="#6B7280" />
                  )}
                  <span style={{ 
                    fontSize: '15px', 
                    fontWeight: '600', 
                    color: '#1F2937'
                  }}>
                    {areaName}
                  </span>
                  <span style={{
                    backgroundColor: '#E5E7EB',
                    color: '#6B7280',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '12px'
                  }}>
                    {plays.length} plays
                  </span>
                  <span style={{
                    backgroundColor: '#FEF3C7',
                    color: '#92400E',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '600'
                  }}>
                    Legacy — view/delete only
                  </span>
                </div>
                
                <div style={{ display: 'flex', gap: '8px' }} onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => handleDeleteArea(areaName)}
                    title="Delete solution area"
                    style={{
                      backgroundColor: '#FEE2E2',
                      color: '#DC2626',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '6px 10px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '13px'
                    }}
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>
              </div>

              {/* Solution Plays List */}
              {expandedAreas[areaName] && plays.length > 0 && (
                <div style={{ paddingLeft: '52px', paddingRight: '20px', paddingBottom: '12px' }}>
                  {plays.map((play, playIndex) => (
                    <div
                      key={playIndex}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 12px',
                        backgroundColor: playIndex % 2 === 0 ? '#FFFFFF' : '#F9FAFB',
                        borderRadius: '6px',
                        marginBottom: '4px'
                      }}
                    >
                      <span style={{ fontSize: '14px', color: '#374151', minWidth: 0, overflowWrap: 'anywhere' }}>
                        {play}
                      </span>
                      <button
                        onClick={() => handleDeletePlay(areaName, play)}
                        title="Delete solution play"
                        style={{
                          backgroundColor: 'transparent',
                          color: '#9CA3AF',
                          border: 'none',
                          padding: '4px',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#FEE2E2';
                          e.target.style.color = '#DC2626';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = '#9CA3AF';
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Empty state for plays */}
              {expandedAreas[areaName] && plays.length === 0 && (
                <div style={{ 
                  paddingLeft: '52px', 
                  paddingRight: '20px', 
                  paddingBottom: '16px',
                  color: '#9CA3AF',
                  fontSize: '14px',
                  fontStyle: 'italic'
                }}>
                  No solution plays defined for this area (or all have been deleted).
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Conversations Manager (FY27) */}
      <div style={{ marginTop: '40px', marginBottom: '24px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937', marginBottom: '4px' }}>
          Conversations Manager
        </h2>
        <p style={{ fontSize: '14px', color: '#6B7280' }}>
          FY27 replacement for Solution Play — 2 fixed, independent attributes (not tied to Solution Area). Add the values each new upload can choose from.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
        {Object.keys(conversations).map(group => (
          <div key={group} style={{ minWidth: 0, backgroundColor: '#FFFFFF', borderRadius: '12px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
              <span style={{ fontSize: '15px', fontWeight: '600', color: '#1F2937' }}>{group}</span>
              {addingConversationTo !== group ? (
                <button
                  onClick={() => setAddingConversationTo(group)}
                  data-testid={`add-conversation-btn-${group}`}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#1B5E5F', color: '#FFFFFF', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}
                >
                  <Plus size={14} />
                  Add Value
                </button>
              ) : null}
            </div>

            {addingConversationTo === group && (
              <div style={{ padding: '12px 20px', backgroundColor: '#F0FDF4', display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  value={newConversationInputs[group] || ''}
                  onChange={(e) => setNewConversationInputs(prev => ({ ...prev, [group]: e.target.value }))}
                  placeholder="e.g. AI Transformation"
                  autoFocus
                  data-testid={`new-conversation-input-${group}`}
                  style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #D1D5DB', fontSize: '14px' }}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddConversation(group)}
                />
                <button
                  onClick={() => handleAddConversation(group)}
                  data-testid={`confirm-add-conversation-btn-${group}`}
                  style={{ backgroundColor: '#10B981', color: '#FFFFFF', border: 'none', borderRadius: '6px', padding: '8px', cursor: 'pointer' }}
                >
                  <Check size={16} />
                </button>
                <button
                  onClick={() => { setAddingConversationTo(null); setNewConversationInputs(prev => ({ ...prev, [group]: '' })); }}
                  style={{ backgroundColor: '#EF4444', color: '#FFFFFF', border: 'none', borderRadius: '6px', padding: '8px', cursor: 'pointer' }}
                >
                  <X size={16} />
                </button>
              </div>
            )}

            <div style={{ padding: '12px 16px' }}>
              {(conversations[group] || []).length === 0 ? (
                <p style={{ color: '#9CA3AF', fontSize: '13px', fontStyle: 'italic', padding: '8px 4px' }}>No values defined yet. Add one to get started.</p>
              ) : (
                (conversations[group] || []).map((name, idx) => (
                  <div
                    key={name}
                    data-testid={`conversation-row-${group}-${name}`}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '10px 12px', backgroundColor: idx % 2 === 0 ? '#FFFFFF' : '#F9FAFB', borderRadius: '6px', marginBottom: '4px' }}
                  >
                    <span style={{ fontSize: '14px', color: '#374151', minWidth: 0, overflowWrap: 'anywhere' }}>{name}</span>
                    <button
                      onClick={() => handleDeleteConversation(group, name)}
                      title="Delete value"
                      data-testid={`delete-conversation-${group}-${name}`}
                      style={{ backgroundColor: 'transparent', color: '#9CA3AF', border: 'none', padding: '4px', cursor: 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', flexShrink: 0 }}
                      onMouseEnter={(e) => { e.target.style.backgroundColor = '#FEE2E2'; e.target.style.color = '#DC2626'; }}
                      onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = '#9CA3AF'; }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        ))}
      </div>

    </div>
  );
}

export default TaxonomyManager;
